import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Phone", "Laptop", "Accessories"];
const emptyForm = { sku: "", name: "", category: "Phone", unit: "unit", sell_at_cost: false };

export default function Produk() {
  const { user } = useAuth();
  const canManage = ["owner", "branch_manager", "admin_gudang"].includes(user.role);
  const [products, setProducts] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/reports/stock").then((r) => {
      const map = {};
      r.data.rows.forEach((row) => {
        map[row.product_id] = (map[row.product_id] || 0) + row.qty;
      });
      setStockMap(map);
    }).catch(() => {});
  };

  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ sku: p.sku, name: p.name, category: p.category, unit: p.unit, sell_at_cost: !!p.sell_at_cost });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const filtered = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="produk-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Products</h1>
          <p className="mt-1 text-sm text-zinc-500">Master data for phones, laptops, and accessories.</p>
        </div>
        {canManage && (
          <Button data-testid="btn-add-product" onClick={openCreate} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" /> Add Product
          </Button>
        )}
      </div>

      <Input
        data-testid="search-product"
        placeholder="Search name or SKU..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm rounded-sm border-zinc-300 bg-white focus-visible:ring-blue-600"
      />

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="product-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">SKU</th>
              <th className="px-4 py-3 font-medium">Product Name</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Unit</th>
              <th className="px-4 py-3 text-right font-medium">Total Stock</th>
              {canManage && <th className="px-4 py-3 text-right font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-zinc-400" data-testid="product-empty">
                  No products yet
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`product-row-${p.sku}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{p.sku}</td>
                <td className="px-4 py-2.5 font-medium text-zinc-900">
                  {p.name}
                  {p.sell_at_cost && (
                    <span className="ml-2 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`product-at-cost-${p.sku}`}>AT COST</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-0.5 text-xs font-semibold text-zinc-600">{p.category}</span>
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{p.unit}</td>
                <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{stockMap[p.id] || 0}</td>
                {canManage && (
                  <td className="px-4 py-2.5 text-right">
                    <button data-testid={`btn-edit-product-${p.sku}`} onClick={() => openEdit(p)} className="mr-1 rounded-sm p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-blue-600">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`btn-delete-product-${p.sku}`} onClick={() => remove(p)} className="rounded-sm p-1.5 text-zinc-500 transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription className="sr-only">Product details form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>SKU</Label>
              <Input data-testid="product-form-sku" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="IP15-128" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Product Name</Label>
              <Input data-testid="product-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="iPhone 15 128GB" className="rounded-sm border-zinc-300" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input data-testid="product-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
            </div>
            <label className="flex items-start gap-2 rounded-sm border border-zinc-200 px-3 py-2.5">
              <input
                type="checkbox"
                data-testid="product-form-sell-at-cost"
                checked={form.sell_at_cost}
                onChange={(e) => setForm({ ...form, sell_at_cost: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-blue-600"
              />
              <span>
                <span className="block text-sm font-medium text-zinc-800">Sell at cost (COGS)</span>
                <span className="block text-xs text-zinc-500">Invoice price auto-fills with the FIFO purchase cost.</span>
              </span>
            </label>
            <DialogFooter>
              <Button data-testid="product-form-submit" type="submit" disabled={saving} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
